import React from 'react';
import { css } from 'styled-components';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { User } from 'lib/User';

import { FRUIT_CATEGORY_ID } from 'constants/categories-constants';
import { UNCOUNTABLE_PRODUCT_IDS } from 'constants/product-constants';
import { ARCHIVED, CREATED, REGULAR } from 'constants/states-app';
import { Notification } from 'lib/Notification';
import { GetBadgesQuery, GetOrganizationQuery, ProductFragmentFragment as Product } from 'data/__generated__';

export function resolvePath(...args: string[]): string {
    return args.join('/');
}

export const regexInternationalNumber = new RegExp(/^([+]|0{2})([17]|[2-689][0-9]{1,2})[0-9]{8,14}$/);

export function isClient(): boolean {
    return !User.isGlobalToteam() && !User.isGlobalTotest();
}

export function formUrl(...args: (string | boolean)[]): (...args: string[]) => string {
    const argv: (string | boolean)[] = [...args];
    const lastArg = argv.pop();
    let isAbsolute: boolean = false;
    if (typeof lastArg === 'boolean') {
        isAbsolute = lastArg;
    } else if (typeof lastArg === 'string') {
        argv.push(lastArg);
    }
    const baseUrl = resolvePath(...(argv as string[]));
    return (...innerArgs: string[]) => {
        return (isAbsolute ? '/' : '') + resolvePath(baseUrl, ...innerArgs);
    };
}

export function breakpointMixin(
    cardWidth: string,
    minWidth: number,
    maxWidth: number = 9999999999,
    marginValue: number = 5,
): ReturnType<typeof css> {
    let width = cardWidth;
    if (marginValue === 5) {
        width = `calc(${cardWidth} - ${marginValue * 2}px)`;
    }
    return css`
        @media screen and (min-width: ${minWidth}px) and (max-width: ${maxWidth}px) {
            width: ${width};
        }
    `;
}

export function capitalize(word: string = ''): string {
    return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
}

export function getInitials(firstName: string = '', lastName: string = ''): string {
    return firstName && lastName ? `${firstName.slice(0, 1)} ${lastName.slice(0, 1)}`.toUpperCase() : '';
}

export function getKeysWithTrueValues(object: Record<string, boolean>): string[] {
    return Object.keys(object).filter((key) => object[key] === true);
}

function getPriceValue(price: number, pricesRange: number[]): { value: string | undefined; label: string | undefined } {
    const priceMin = Math.min(...pricesRange);
    const priceMax = Math.max(...pricesRange);
    const priceAvg = (priceMin + priceMax) / 2;
    const avgMinHalf = (priceMin + priceAvg) / 2;
    const avgMaxHalf = (priceMax + priceAvg) / 2;
    let value, label;

    if (price < avgMinHalf) {
        value = 'low';
        label = '€';
    } else if (price >= avgMinHalf && price < avgMaxHalf) {
        value = 'average';
        label = '€€';
    } else if (price >= avgMaxHalf) {
        value = 'high';
        label = '€€€';
    }

    return {
        value,
        label,
    };
}

export function isCategoryAvailable(category: { state: string }): boolean {
    const LIST_OF_CATEGORY_UNAVAILABLE_STATES = User.isGlobalToteam() ? [ARCHIVED] : [ARCHIVED, CREATED];
    return !LIST_OF_CATEGORY_UNAVAILABLE_STATES.includes(category.state);
}

export function getPortionName(product: { name: string; categoryId: string; _id: string }): string {
    const getFruitName = () => {
        const words = product.name.split(' ');
        return words[0].toLowerCase();
    };
    return product.categoryId === FRUIT_CATEGORY_ID && !UNCOUNTABLE_PRODUCT_IDS.includes(product._id)
        ? getFruitName()
        : 'portion';
}

export function checkIfProductIsAvailable(product: {
    isFreefood: boolean;
    state: string;
    isBeingTested?: boolean;
}): boolean {
    return product.isFreefood && (product.state === REGULAR || (User.isGlobalToteam() && !!product.isBeingTested));
}

export function hasAccessToPrivateProduct(
    product: Product,
    organization: GetOrganizationQuery['organizationOfUser'] | null,
): boolean {
    return product?.access?.includes(organization?._id ?? '');
}

export function getProductFilters(
    products: any[],
    organization: GetOrganizationQuery['organizationOfUser'] | null,
    badges: GetBadgesQuery['badges'] | null,
): {
    badges: { label: string; value: string }[];
    tags: { label: string; value: string }[];
    prices: { value: string; label: string }[];
    brands: { label: string; value: string }[];
} {
    const filteredProducts = products.filter((product) => {
        const isProductPrivate = !!product?.access?.length;
        return (
            checkIfProductIsAvailable(product) &&
            (!isProductPrivate || (isProductPrivate && hasAccessToPrivateProduct(product, organization)))
        );
    });
    const pricesRange = filteredProducts.map((p) => parseFloat((p.price / p.portion).toFixed(2)));
    return filteredProducts.reduce(
        (filters, { badges, tags, brand, price, portion }) => {
            badges &&
                badges.forEach((badge: any) => {
                    if (!filters.badges.find((badgeFilter: any) => badgeFilter.value === badge._id)) {
                        !badge.includes('typename') &&
                            filters.badges.push({
                                label: badge.name,
                                value: badge._id,
                            });
                    }
                });
            tags &&
                tags.forEach((tag: any) => {
                    if (!filters.tags.find((tagFilter: any) => tagFilter.value === tag._id)) {
                        filters.tags.push({
                            label: tag?.name,
                            value: tag?._id,
                        });
                    }
                });
            if (brand && !filters.brands.find((brandFilter: any) => brandFilter.value === brand)) {
                filters.brands.push({
                    label: brand,
                    value: brand,
                });
            }
            if (!isNaN(price)) {
                const pricePerPortion = parseFloat((price / portion).toFixed(2));
                const priceValue = getPriceValue(pricePerPortion, pricesRange).value;
                if (!filters.prices.find((priceFilter: any) => priceFilter.value === priceValue)) {
                    filters.prices.push(getPriceValue(pricePerPortion, pricesRange));
                    filters.prices = filters.prices.sort((a: any, b: any) => a.label.length - b.label.length);
                }
            }
            return filters;
        },
        {
            badges:
                badges?.map((badge: any) => {
                    return { label: badge.name, value: badge._id };
                }) || [],
            tags: [],
            prices: [],
            brands: [],
        },
    );
}

export function getFilteredProducts(
    products: any[],
    organization: GetOrganizationQuery['organizationOfUser'] | null,
    {
        tags,
        badges,
        brands,
        prices,
        search = '',
    }: {
        tags: string[];
        badges: string[];
        brands: string[];
        prices: string[];
        search?: string;
    },
): any[] {
    const filteredProducts = products.filter((product) => {
        const isProductPrivate = !!product?.access?.length;
        return (
            checkIfProductIsAvailable(product) &&
            (!isProductPrivate || (isProductPrivate && hasAccessToPrivateProduct(product, organization)))
        );
    });
    const pricesRange = filteredProducts.map((p) => parseFloat((p.price / p.portion).toFixed(2)));
    const tagsLength = tags.length;
    const badgesLength = badges.length;
    const pricesLength = prices.length;
    const brandsLength = brands.length;
    if (
        (tags && tagsLength) ||
        (badges && badgesLength) ||
        (brands && brandsLength) ||
        (prices && pricesLength) ||
        search
    ) {
        let isFilteredTags = true;
        let isFilteredBadges = true;
        let isFilteredBrands = true;
        let isFilteredPrices = true;
        let isFilteredSearch = true;
        return products.filter((product) => {
            if (tags && tagsLength) {
                isFilteredTags = tags.some((o) => product.tags && product.tags.find(({ _id }: any) => o === _id));
            }
            if (badges && badgesLength) {
                const productBadges = [...product.preferences, ...product.diets];
                isFilteredBadges = badges.some((o) =>
                    productBadges.some((productBadge: any) => productBadge._id === o),
                );
            }
            if (prices && pricesLength) {
                const subscriberPrice =
                    Math.round(((product.unitPrice * product.quantity) / product.portion) * 100) / 100;
                isFilteredPrices = prices.some((o) => o === getPriceValue(subscriberPrice, pricesRange).value);
            }
            if (brands && brandsLength) {
                isFilteredBrands = brands.some((brand) => brand === product.brand);
            }
            if (search) {
                const regExp = new RegExp(search, 'i');
                isFilteredSearch =
                    regExp.test(product.name) || regExp.test(product.fullname) || regExp.test(product.description);
            }
            return isFilteredTags && isFilteredBadges && isFilteredBrands && isFilteredPrices && isFilteredSearch;
        });
    }
    return products;
}

export function debounce<T extends (...args: any[]) => void>(func: T, wait: number): T & { cancel: () => void } {
    let timeout: ReturnType<typeof setTimeout>;
    const debounceFunction = (...args: any[]) => {
        if (args[0] && args[0].constructor && args[0].constructor.name === 'SyntheticEvent') {
            args[0].persist();
        }
        const later = () => {
            timeout = null as any;
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
    debounceFunction.cancel = () => {
        clearTimeout(timeout);
    };
    return debounceFunction as T & { cancel: () => void };
}

export function parseQueryStrings(queryString: string): Record<string, string> {
    if (!queryString) {
        return {};
    }
    const params = new URLSearchParams(queryString);
    // Use Array.from for compatibility with ES5/ES2015
    const searchParamsObject = Array.from(params.entries()).reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
    return searchParamsObject;
}

export const handleTextInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    setState: (state: any) => void,
    state: Record<string, any>,
): void => {
    const {
        target: { value, name },
    } = e;
    setState({
        ...state,
        [name]: value,
    });
};

/**
 * Function that gets the difference in time between two hours (eg. "9:00", "10:30" -> 1,5)
 * @param {String} time1 String representing the time (eg. "9:00")
 * @param {String} time2 String representing the time (eg. "10:30")
 */
export function getTimeDifference(time1: string | number, time2: string | number): number {
    const [hour1, minute1] = String(time1).split(':').map(Number);
    const [hour2, minute2] = String(time2).split(':').map(Number);
    return hour2 - hour1 + (minute2 - minute1) / 60;
}

/**
 * Function that returns the endTime = startTime + deliverySlot
 * @param {String} startTime A string that represents an hour (eg. "9:30")
 * @param {Number} deliverySlot A number that represents the time between two slots
 */
export function getEndTime(startTime: string, deliverySlot: number): string {
    const [hour, minute] = startTime.split(':').map(Number);
    const startHourAsNumber = hour + minute / 60;
    const endTimeAsNumber = startHourAsNumber + deliverySlot;
    const endHour = Math.floor(endTimeAsNumber);
    const endMinute = endTimeAsNumber % 1 === 0 ? '00' : '30';
    return `${endHour < 10 ? `0${endHour}` : endHour}:${endMinute}`;
}

export function objectChangeCheck(
    originalObject: Record<string, any>,
    changedObject: Record<string, any>,
    fieldsToCheck: string[] = [],
): boolean {
    if (fieldsToCheck.length) {
        for (let i = 0; i < fieldsToCheck.length; i++) {
            const key = fieldsToCheck[i];
            if (!originalObject[key] && !!changedObject[key]) {
                return true;
            }
            if (originalObject[key] !== changedObject[key]) {
                return true;
            }
        }
        return false;
    }
    const originalKeyValues = Object.keys(originalObject);
    const changedKeyValues = Object.keys(changedObject);
    if (originalKeyValues.length !== changedKeyValues.length) {
        return true;
    }
    for (let i = 0; i < originalKeyValues.length; i++) {
        const key = originalKeyValues[i];
        const isStringComparison = typeof originalObject[key] === 'string' && typeof changedObject[key] === 'string';
        if (isStringComparison && originalObject[key] !== changedObject[key]) {
            return true;
        }
    }
    return false;
}

export function objectIsInObject(originalObject: Record<string, any>, objectToCheck: Record<string, any>): boolean {
    const keyValues = Object.keys(objectToCheck);
    for (let i = 0; i < keyValues.length; i++) {
        const key = keyValues[i];
        if (originalObject[key] !== objectToCheck[key]) {
            return false;
        }
    }
    return true;
}

export function extractKeysFromObject(object: Record<string, any>, keys: string[]): Record<string, any> {
    return keys.reduce(
        (reducedObject, key) => ({
            ...reducedObject,
            [key]: object[key],
        }),
        {},
    );
}

export function createSelectOptionsWithValue(
    optionsArray: { value: string; label: string }[],
    name: string,
): { value: string; label: string; name: string }[] {
    return optionsArray.map(({ value, label }) => ({
        value: value,
        label: label,
        name,
    }));
}

export function pluralize(words: string, count: number): string {
    const splitWords = words.split(' ');
    return count > 1 ? splitWords.map((word) => `${word}s`).join(' ') : words;
}

export function decimals(n: number | string, d: number | string): string | false {
    let num: number;
    let digits: number;
    if (typeof n === 'string') {
        num = parseFloat(n);
    } else {
        num = n;
    }
    if (typeof d === 'string') {
        digits = parseInt(d, 10);
    } else {
        digits = d;
    }
    if (isNaN(num) || isNaN(digits)) {
        return false;
    }
    if (num.toString().includes('.')) {
        return num.toFixed(digits);
    }
    return num.toFixed(0);
}

export function validateEmail(errorMessage: string): (email: string | undefined) => boolean | string {
    return function emailValidate(email: string | undefined): boolean | string {
        const trimmedEmail = email?.trim();
        return (
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                trimmedEmail ?? '',
            ) || errorMessage
        );
    };
}

export function validatePhone(errorMessage: string): (phoneNumber: string) => boolean | string {
    return function phoneValidate(phoneNumber: string): boolean | string {
        const errorMessageToUse =
            phoneNumber[0] === '+' || (phoneNumber[0] === '0' && phoneNumber[1] === '0')
                ? errorMessage
                : "Veuillez rajouter l'indicatif téléphonique";
        const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, 'FR');
        return (
            (parsedPhoneNumber?.isValid() && parsedPhoneNumber?.country === 'FR') ||
            regexInternationalNumber.test(phoneNumber) ||
            errorMessageToUse
        );
    };
}

// Affiche seulement : l'errorLink a déjà trié l'erreur (Sentry pour une erreur de contrat, breadcrumb pour un
// refus métier ou une coupure réseau).
export function HandleNotification(error: any): void {
    const { graphQLErrors, networkError } = error;
    const actualErrorToBeSent = graphQLErrors?.[0] || null;
    if (networkError) {
        Notification(new Error('Oops, une erreur est survenue. Merci de réessayer.'));
        return;
    }
    if (actualErrorToBeSent) {
        Notification(actualErrorToBeSent);
    }
}

export function displayQuantityKg(displayKg: boolean, netWeight: number, quantity: number): string {
    if (displayKg) {
        return Number(quantity * netWeight)
            .toFixed(3)
            .replace('.', ',')
            .replace(',000', '');
    }
    return Number(quantity).toFixed(3).replace('.', ',').replace(',000', '');
}

export function getKgs(displayKg: boolean, netWeight: number, quantity: number): string {
    return displayQuantityKg(displayKg, netWeight, quantity).split(',')[0];
}

export function getGrams(displayKg: boolean, netWeight: number, quantity: number): string {
    return displayQuantityKg(displayKg, netWeight, quantity).split(',')[1];
}

export function displayQuantityPrice(number: string | boolean): string {
    return Number(number).toFixed(2).replace('.', ',');
}

export function getEuros(number: string | boolean): string {
    return displayQuantityPrice(number).split(',')[0];
}

export function getCents(number: string | boolean): string {
    return displayQuantityPrice(number).split(',')[1];
}

export function limitToNcharacters(str: string, n: number = 200): string {
    return str.substring(0, n) + (str.length >= n ? '...' : '');
}

export function getUrlParameters(pathname: string): {
    totemId?: string;
    navItem?: string;
    orderId?: string;
    productId?: string;
} {
    const [totemId, navItem, orderId, productId] = pathname
        .split('/')
        .filter((urlParameter) => urlParameter && urlParameter !== 'totem');
    return {
        totemId,
        navItem,
        orderId,
        productId,
    };
}
